/**
 * useDeptKPILogic - 部门KPI配置页面的核心业务逻辑
 */
import { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import { kpiSettingsAPI, DepartmentKPIProfile, KPIDimension, PerformancePeriod, Department } from '../../../../services/kpiSettingsService';

export interface DeptKPILogicState {
    departments: Department[];
    periods: PerformancePeriod[];
    dimensions: KPIDimension[];
    profiles: DepartmentKPIProfile[];
    selectedPeriodId?: number;
    editingData: Record<number, Partial<DepartmentKPIProfile>>;
    loading: boolean;
    saving: boolean;
}

export function useDeptKPILogic() {
    const [departments, setDepartments] = useState<Department[]>([]);
    const [periods, setPeriods] = useState<PerformancePeriod[]>([]);
    const [dimensions, setDimensions] = useState<KPIDimension[]>([]);
    const [profiles, setProfiles] = useState<DepartmentKPIProfile[]>([]);
    const [selectedPeriodId, setSelectedPeriodId] = useState<number>();
    const [editingData, setEditingData] = useState<Record<number, Partial<DepartmentKPIProfile>>>({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    // 加载基础数据
    const loadBaseData = useCallback(async () => {
        try {
            const [deptRes, periodRes, dimRes] = await Promise.all([
                kpiSettingsAPI.getDepartments(),
                kpiSettingsAPI.getPeriods(),
                kpiSettingsAPI.getDimensions(),
            ]);
            const deptData = (deptRes.data as any)?.results || deptRes.data || [];
            setDepartments(Array.isArray(deptData) ? deptData : []);

            const periodsData = (periodRes.data as any)?.results || periodRes.data || [];
            setPeriods(Array.isArray(periodsData) ? periodsData : []);

            const dimData = (dimRes.data as any)?.results || dimRes.data || [];
            setDimensions(Array.isArray(dimData) ? dimData : []);

            if (Array.isArray(periodsData) && periodsData.length > 0 && !selectedPeriodId) {
                setSelectedPeriodId(periodsData[0].id);
            }
        } catch (error) {
            message.error('加载数据失败');
        }
    }, [selectedPeriodId]);

    // 加载配置
    const loadProfiles = useCallback(async () => {
        if (!selectedPeriodId) return;
        setLoading(true);
        try {
            const res = await kpiSettingsAPI.getProfilesByPeriod(selectedPeriodId);
            setProfiles(res.data);
            const editing: Record<number, Partial<DepartmentKPIProfile>> = {};
            res.data.forEach(p => {
                editing[p.department] = { ...p };
            });
            setEditingData(editing);
        } catch (error) {
            message.error('加载配置失败');
        } finally {
            setLoading(false);
        }
    }, [selectedPeriodId]);

    useEffect(() => {
        loadBaseData();
    }, [loadBaseData]);

    useEffect(() => {
        if (selectedPeriodId) {
            loadProfiles();
        }
    }, [selectedPeriodId, loadProfiles]);

    // 处理单元格变更
    const handleChange = (deptId: number, field: string, value: any) => {
        setEditingData(prev => ({
            ...prev,
            [deptId]: {
                ...prev[deptId],
                department: deptId,
                period: selectedPeriodId,
                [field]: value,
            }
        }));
    };

    // 计算总权重
    const getTotalWeight = (deptId: number): number => {
        const data = editingData[deptId] || {};
        return (Number(data.kpi1_weight) || 0) +
            (Number(data.kpi2_weight) || 0) +
            (Number(data.kpi3_weight) || 0) +
            (Number(data.kpi4_weight) || 0) +
            ((data.attitude_weight !== undefined && data.attitude_weight !== null) ? Number(data.attitude_weight) : 15);
    };

    // 保存配置
    const handleSave = async () => {
        const invalidDepts = departments.filter(d => {
            const total = getTotalWeight(d.id);
            const data = editingData[d.id];
            if (data && (data.kpi1 || data.kpi2 || data.kpi3 || data.kpi4)) {
                return Math.abs(total - 100) > 0.01;
            }
            return false;
        });

        if (invalidDepts.length > 0) {
            message.error(`以下部门权重不等于100%: ${invalidDepts.map(d => d.name).join(', ')}`);
            return;
        }

        setSaving(true);
        try {
            const profilesPayload = Object.values(editingData).map(data => ({
                department_id: data.department,
                period_id: selectedPeriodId,
                kpi1: data.kpi1,
                kpi1_weight: data.kpi1_weight || 0,
                kpi2: data.kpi2,
                kpi2_weight: data.kpi2_weight || 0,
                kpi3: data.kpi3,
                kpi3_weight: data.kpi3_weight || 0,
                kpi4: data.kpi4,
                kpi4_weight: data.kpi4_weight || 0,
                attitude_weight: data.attitude_weight !== undefined ? data.attitude_weight : 15,
            })).filter(p => p.kpi1 || p.kpi2 || p.kpi3 || p.kpi4 || p.attitude_weight);

            const res = await kpiSettingsAPI.batchSaveProfiles(profilesPayload);
            if (res.data && res.data.errors && res.data.errors.length > 0) {
                message.warning(`保存完成，但有部分失败: ${res.data.errors.join('; ')}`);
            } else {
                message.success('保存成功');
            }
            loadProfiles();
        } catch (error) {
            message.error('保存失败');
        } finally {
            setSaving(false);
        }
    };

    // 复制上期配置
    const handleCopyFromPrevious = async () => {
        const currentIndex = periods.findIndex(p => p.id === selectedPeriodId);
        if (currentIndex <= 0) {
            message.warning('没有可复制的上期配置');
            return;
        }
        const prevPeriod = periods[currentIndex - 1];
        try {
            await kpiSettingsAPI.copyFromPeriod(prevPeriod.id, selectedPeriodId!);
            message.success('复制成功');
            loadProfiles();
        } catch (error) {
            message.error('复制失败');
        }
    };

    return {
        // State
        departments,
        periods,
        dimensions,
        profiles,
        selectedPeriodId,
        editingData,
        loading,
        saving,
        // Actions
        setSelectedPeriodId,
        handleChange,
        getTotalWeight,
        handleSave,
        handleCopyFromPrevious,
        loadProfiles,
        loadBaseData,
    };
}
